import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const DISMISS_KEY = 'notification_prompt_dismissed_at';
const RE_PROMPT_DAYS = 7;

export const NotificationPrompt: React.FC = () => {
  const { isSupported, isStandalone, permissionState, isSubscribed, subscribe } = usePushNotifications();
  const [dismissed, setDismissed] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only show in standalone PWA mode, when permission hasn't been asked yet
    if (!isSupported || !isStandalone || permissionState !== 'default' || isSubscribed) {
      setDismissed(true);
      return;
    }

    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
      if (daysSince < RE_PROMPT_DAYS) {
        setDismissed(true);
        return;
      }
    }

    setDismissed(false);
  }, [isSupported, isStandalone, permissionState, isSubscribed]);

  if (dismissed) return null;

  const handleEnable = async () => {
    setLoading(true);
    await subscribe();
    setLoading(false);
    setDismissed(true);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setDismissed(true);
  };

  return (
    <div className="mx-4 mt-4 p-4 rounded-lg border bg-card text-card-foreground shadow-sm flex items-start gap-3">
      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
        <Bell className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">Enable notifications</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Stay updated on your leads, tasks, and enrichment progress.
        </p>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={handleEnable} disabled={loading}>
            {loading ? 'Enabling...' : 'Enable'}
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDismiss}>
            Not now
          </Button>
        </div>
      </div>
      <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
